import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import {
  Button, Chip, Input,
} from '../../components/Ui';
import { Reveal, Pop } from '../../components/Motion';
import ImageUploader from '../../components/ImageUploader';
import VideoUploader from '../../components/VideoUploader';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';

const TYPES = ['car', 'bike', 'scooter', 'auto', 'commercial'];
const FUELS = ['petrol', 'diesel', 'cng', 'electric', 'hybrid'];
const TRANSMISSIONS = ['manual', 'automatic'];
const CONDITIONS = ['new', 'excellent', 'good', 'fair'];

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const PostVehiclePage = () => {
  const navigate = useNavigate();
  const { location } = useApp();
  const toast = useToast();

  const [done, setDone] = useState(false);
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '', vehicleType: 'car', brand: '', model: '', year: '', price: '',
    kmDriven: '', fuelType: 'petrol', transmission: 'manual', owners: '1',
    condition: 'good', description: '', city: location.city || '', negotiable: true,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3) { setError('Enter a listing title'); return; }
    if (!form.price) { setError('Enter the asking price'); return; }
    setSaving(true);
    try {
      await api.vehicles.create({
        ...form,
        images,
        video,
        year: Number(form.year) || undefined,
        price: Number(form.price),
        kmDriven: Number(form.kmDriven) || 0,
        owners: Number(form.owners) || 1,
        coordinates: [location.longitude, location.latitude],
      });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(err.message, 'Could not create listing');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="center-page">
        <div style={{ maxWidth: 440 }}>
          <Pop>
            <div
              style={{
                width: 100, height: 100, borderRadius: 50, background: colors.successBg,
                display: 'grid', placeItems: 'center', fontSize: 44, margin: '0 auto',
              }}
            >
              🚗
            </div>
          </Pop>
          <h1 className="h1" style={{ marginTop: 20 }}>Listing submitted</h1>
          <p className="t-body" style={{ color: colors.muted, marginTop: 12, lineHeight: 1.6 }}>
            Our team reviews vehicle listings before they go live. You will be notified once{' '}
            <strong style={{ color: colors.ink }}>{form.title}</strong> is approved.
          </p>
          <div className="col" style={{ gap: 12, marginTop: 26 }}>
            <Button title="View my listings" size="lg" full onClick={() => navigate('/my-vehicles', { replace: true })} />
            <Button title="Back to vehicles" variant="light" full onClick={() => navigate('/vehicles')} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <PageHeader title="Sell your vehicle" subtitle="Reach buyers in your area" />

      <Reveal className="card sh-sm">
        <form onSubmit={submit}>
          <ImageUploader
            folder="misc"
            label="Vehicle photos"
            hint="Buyers skip listings without photos. Add the front, side, interior and odometer."
            value={images}
            onChange={setImages}
            max={8}
          />
          <VideoUploader
            label="Walkround video (optional)"
            hint="Walk around the vehicle, show the interior and start the engine."
            folder="vehicle"
            value={video}
            onChange={setVideo}
          />

          <label className="field-label">Vehicle type</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
            {TYPES.map((t) => (
              <Chip
                key={t}
                label={cap(t)}
                active={form.vehicleType === t}
                onClick={() => setForm({ ...form, vehicleType: t })}
              />
            ))}
          </div>

          <Input
            label="Listing title"
            icon="🚗"
            placeholder="e.g. Maruti Swift VXI 2019"
            value={form.title}
            onChange={(e) => { setForm({ ...form, title: e.target.value }); setError(''); }}
            error={error && !form.title ? error : ''}
          />

          <div className="form-row">
            <Input
              label="Brand"
              className="grow"
              placeholder="Maruti"
              value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
            />
            <Input
              label="Model"
              className="grow"
              placeholder="Swift"
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
            />
          </div>

          <div className="form-row">
            <Input
              label="Year"
              className="grow"
              inputMode="numeric"
              maxLength={4}
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value.replace(/\D/g, '') })}
            />
            <Input
              label="Km driven"
              className="grow"
              inputMode="numeric"
              value={form.kmDriven}
              onChange={(e) => setForm({ ...form, kmDriven: e.target.value.replace(/\D/g, '') })}
            />
          </div>

          <Input
            label="Asking price (₹)"
            icon="💰"
            inputMode="numeric"
            placeholder="450000"
            value={form.price}
            onChange={(e) => { setForm({ ...form, price: e.target.value.replace(/\D/g, '') }); setError(''); }}
            error={error && form.title && !form.price ? error : ''}
          />

          <label className="field-label">Fuel type</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
            {FUELS.map((f) => (
              <Chip key={f} label={cap(f)} active={form.fuelType === f} onClick={() => setForm({ ...form, fuelType: f })} />
            ))}
          </div>

          <label className="field-label">Transmission</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
            {TRANSMISSIONS.map((t) => (
              <Chip key={t} label={cap(t)} active={form.transmission === t} onClick={() => setForm({ ...form, transmission: t })} />
            ))}
          </div>

          <label className="field-label">Condition</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
            {CONDITIONS.map((c) => (
              <Chip key={c} label={cap(c)} active={form.condition === c} onClick={() => setForm({ ...form, condition: c })} />
            ))}
          </div>

          <div className="form-row">
            <Input
              label="Number of owners"
              className="grow"
              inputMode="numeric"
              value={form.owners}
              onChange={(e) => setForm({ ...form, owners: e.target.value.replace(/\D/g, '') })}
            />
            <Input
              label="City"
              className="grow"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </div>

          <Input
            label="Description (optional)"
            textarea
            rows={4}
            placeholder="Service history, accessories, reason for selling…"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <Chip
            label={form.negotiable ? '✓ Price is negotiable' : 'Price is negotiable'}
            active={form.negotiable}
            onClick={() => setForm({ ...form, negotiable: !form.negotiable })}
          />

          <Button
            title="Submit listing"
            type="submit"
            loading={saving}
            size="lg"
            full
            style={{ marginTop: 20 }}
          />
        </form>
      </Reveal>
    </div>
  );
};

export default PostVehiclePage;
